import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  CHART_COLORS,
  CHART_TYPES,
  DEFAULT_RECHARTS_COLORS,
  DRILLDOWN_KEY,
  getYAxisLabelConfig,
  shouldShowYAxisLabel,
} from '../constants';
import { useContainerSize } from '../../../../../../js/useContainerSize';
import CustomTick from '../CustomTick';
import CustomTooltip from '../CustomTooltip';
import EmptyState from '../Empty/EmptyState';
import { formatLabel } from '../utils';
import { GROUP_OPTIONS } from '../../modals/chartCreationConstants';
import { getWorkspaceCustomFields } from '../../../../../../redux/workspace/selectors';
import LoadingState from '../Loadinng/LoadingState';

const CHART_MARGIN = { top: 20, right: 20, left: 0, bottom: 10 };
const CHART_MARGIN_WITH_LEGEND = { top: 20, right: 50, left: 0, bottom: 10 };

const GRID_CONFIG = {
  strokeDasharray: '3 3',
  stroke: CHART_COLORS.GRID_STROKE,
  horizontal: true,
  vertical: false,
};

const AXIS_LINE_CONFIG = { stroke: CHART_COLORS.AXIS_STROKE };
const TICK_LINE_CONFIG = { stroke: CHART_COLORS.AXIS_STROKE };
const Y_TICK_CONFIG = { fill: CHART_COLORS.AXIS_STROKE, fontSize: 12 };

const X_AXIS_CONFIG = {
  angle: -45,
  textAnchor: 'end',
  dx: -6,
  dy: -2,
};

const BAR_CONFIG = {
  radius: [4, 4, 0, 0],
  barSize: 30,
  cursor: 'pointer',
};

const STACKED_BAR_CONFIG = {
  barSize: 30,
  cursor: 'pointer',
};

const GROUPED_BAR_CONFIG = {
  radius: [4, 4, 0, 0],
  barSize: 30,
  cursor: 'pointer',
};

const TOOLTIP_CONFIG = { fill: 'transparent' };

// One legend row: 16px of line-height (`leading-4`) + the 6px flex gap.
const LEGEND_ITEM_HEIGHT = 22;
// Keeps the list clear of the plot's top/bottom edges instead of running
// flush into them.
const LEGEND_VERTICAL_RESERVE = 24;
// Always worth showing at least a couple of series before the "+N more"
// row, even on a very short card.
const MIN_LEGEND_ITEMS = 2;

const LegendRow = ({ entry }) => (
  <div className="flex items-center gap-1.5 min-w-0">
    <div
      className="w-2.5 h-2.5 rounded-sm shrink-0"
      style={{ backgroundColor: entry.color }}
    />
    {/* `min-w-0` is what actually lets `truncate` clip a long series name —
        a flex child defaults to `min-width: auto` and refuses to shrink. */}
    <span className="text-gray-600 leading-4 truncate min-w-0" title={entry.value}>
      {entry.value}
    </span>
  </div>
);

/**
 * Builds the vertical legend renderer.
 *
 * A grouped/stacked chart can have far more series than the card is tall
 * (one per project, say), and recharts sizes its legend purely from the
 * content — so an unbounded list visibly overflows the card. Two behaviours,
 * per the widget it is rendered in:
 *
 * - card (`scrollable` false): render as many rows as actually fit the
 *   measured height, then a "+N more" row. Scrolling inside a small
 *   dashboard tile is easy to miss, and the full set is one click away in
 *   the fullscreen view.
 * - fullscreen (`scrollable` true): render every series and let the list
 *   scroll — there the legend is the point, so nothing should be hidden.
 */
const makeColoredLegend = ({ availableHeight, scrollable }) =>
  function ColoredLegend(props) {
    const payload = props?.payload || [];
    if (!payload.length) return null;

    if (scrollable) {
      return (
        <div
          className="flex flex-col gap-1.5 text-xs pl-3 overflow-y-auto max-w-[220px]"
          style={availableHeight ? { maxHeight: availableHeight } : undefined}
        >
          {payload.map((entry) => (
            <LegendRow key={entry.value} entry={entry} />
          ))}
        </div>
      );
    }

    const capacity = availableHeight
      ? Math.floor(
          (availableHeight - LEGEND_VERTICAL_RESERVE) / LEGEND_ITEM_HEIGHT,
        )
      : payload.length;

    if (payload.length <= capacity) {
      return (
        <div className="flex flex-col gap-1.5 text-xs pl-3 max-w-[220px]">
          {payload.map((entry) => (
            <LegendRow key={entry.value} entry={entry} />
          ))}
        </div>
      );
    }

    // One row is given up to the "+N more" line itself.
    const visible = payload.slice(
      0,
      Math.max(MIN_LEGEND_ITEMS, capacity - 1),
    );
    const hidden = payload.slice(visible.length);

    return (
      <div className="flex flex-col gap-1.5 text-xs pl-3 max-w-[220px]">
        {visible.map((entry) => (
          <LegendRow key={entry.value} entry={entry} />
        ))}
        <div
          className="text-gray-400 leading-4 pl-4 truncate"
          title={hidden.map((entry) => entry.value).join(', ')}
        >
          {`+${hidden.length} more`}
        </div>
      </div>
    );
  };

const CustomActiveBar = (props) => (
  <Rectangle {...props} stroke={props.fill} strokeWidth={2} opacity={0.8} />
);

const StyledXAxis = ({
  label,
  labelEntities,
  labelType,
  workspaceCustomFields,
}) => {
  const isAssigneeTypeLabel = useMemo(() => {
    if (labelType?.includes(GROUP_OPTIONS.CUSTOM_FIELD_PREFIX)) {
      const customFieldId = labelType?.split(GROUP_OPTIONS.CUSTOM_FIELD_PREFIX)?.[1];
      const customFieldType = workspaceCustomFields?.find(
        (customField) => customField?._id === customFieldId,
      )?.type;
      return customFieldType === GROUP_OPTIONS.ASSIGNEE;
    }
    return [GROUP_OPTIONS.ASSIGNEE, GROUP_OPTIONS.CREATOR].includes(labelType);
  }, [labelType, workspaceCustomFields]);

  const getTextLabelHeight = useCallback(() => {
    const longestLabel = Object.values(labelEntities || {}).reduce((max, item) => {
      const len = String(item.label ?? item.tag ?? item.title ?? '').length;
      return len > max ? len : max;
    }, 0);

    return Math.min(Math.max(longestLabel * 8, 20), 80);
  }, [labelEntities]);

  return (
    <XAxis
      dataKey="name"
      {...X_AXIS_CONFIG}
      interval="preserveStartEnd"
      minTickGap={isAssigneeTypeLabel ? 15 : undefined}
      height={isAssigneeTypeLabel ? 30 : getTextLabelHeight()}
      label={{ value: label, position: 'insideBottom', offset: -10 }}
      tick={(props) => (
        <CustomTick
          labelEntities={labelEntities}
          labelType={labelType}
          dx={-6}
          dy={-2}
          {...props}
        />
      )}
      axisLine={AXIS_LINE_CONFIG}
      tickLine={TICK_LINE_CONFIG}
    />
  );
};

const xAxisMapStateToProps = (state) => ({
  workspaceCustomFields: getWorkspaceCustomFields(state),
});

const ConnectedStyledXAxis = connect(xAxisMapStateToProps)(StyledXAxis);

const StyledYAxis = ({ label }) => (
  <YAxis
    allowDecimals={false}
    axisLine={AXIS_LINE_CONFIG}
    tickLine={TICK_LINE_CONFIG}
    tick={Y_TICK_CONFIG}
    label={label ? getYAxisLabelConfig(label) : undefined}
  />
);

const BarGraph = ({
  chartData,
  colors = DEFAULT_RECHARTS_COLORS,
  isLoading,
  stacked = false,
  grouped = false,
  yAxisLabel = '',
  xAxisLabel = '',
  xLabelType,
  xLabelEntities,
  stackByType,
  stackByEntities,
  handleSliceClick = () => {},
  isFullscreen = false,
}) => {
  const intl = useIntl();
  const wrapperRef = useRef(null);
  const [tooltipY, setTooltipY] = useState(undefined);
  // Callback ref, not `useEffect` + `useRef`: this component returns early
  // while loading/empty, so the wrapper div does not exist on mount — a
  // mount-time effect would find a null ref and never observe anything.
  const { ref: sizeRef, size: containerSize } = useContainerSize();

  const setWrapperRef = useCallback(
    (node) => {
      wrapperRef.current = node;
      sizeRef(node);
    },
    [sizeRef],
  );

  const handleMouseMove = useCallback((e) => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setTooltipY(e.clientY - rect.top + 15);
    }
  }, []);

  const handleMouseLeave = useCallback(() => setTooltipY(undefined), []);

  // Rebuilt only when the measured height or the render context changes —
  // recharts re-measures its legend whenever this identity changes.
  const legendConfig = useMemo(
    () => ({
      layout: 'vertical',
      verticalAlign: 'middle',
      align: 'right',
      content: makeColoredLegend({
        availableHeight: containerSize.height,
        scrollable: isFullscreen,
      }),
    }),
    [containerSize.height, isFullscreen],
  );

  const groupKeys = useMemo(() => {
    if ((!stacked && !grouped) || !chartData?.length) return [];

    const keys = new Set();
    chartData.forEach((item) => {
      Object.keys(item || {}).forEach((key) => {
        // Every key that is not chart metadata is a stack/group series.
        if (
          key !== 'name' &&
          key !== 'group' &&
          key !== 'taskIds' &&
          key !== DRILLDOWN_KEY
        ) {
          keys.add(key);
        }
      });
    });
    return Array.from(keys);
  }, [chartData, stacked, grouped]);

  if (isLoading) {
    return (
      <LoadingState
        type={
          stacked
            ? CHART_TYPES.STACKED_BAR
            : grouped
              ? CHART_TYPES.GROUPED_BAR
              : CHART_TYPES.BAR
        }
      />
    );
  }

  if (!chartData?.length) {
    return (
      <EmptyState
        type={
          stacked
            ? CHART_TYPES.STACKED_BAR
            : grouped
              ? CHART_TYPES.GROUPED_BAR
              : CHART_TYPES.BAR
        }
      />
    );
  }

  const tasksLabel = intl.formatMessage({
    id: 'TASKS',
    defaultMessage: 'Tasks',
  });

  const formatLegendValue = (value) => {
    return formatLabel(
      value,
      stackByType || xLabelType,
      stackByEntities || xLabelEntities,
    );
  };

  const showLegend = stacked || grouped;
  const chartMargin = showLegend ? CHART_MARGIN_WITH_LEGEND : CHART_MARGIN;
  // Hidden when the card is too short for the rotated title to render
  // without being clipped / colliding with the top tick (see constants.js).
  const showYAxisLabel = shouldShowYAxisLabel(yAxisLabel, containerSize);

  return (
    <div ref={setWrapperRef} style={{ width: '100%', height: '100%' }} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={chartData} margin={chartMargin}>
        <CartesianGrid {...GRID_CONFIG} />
        <ConnectedStyledXAxis
          label={xAxisLabel}
          labelType={xLabelType}
          labelEntities={xLabelEntities}
        />
        <StyledYAxis label={showYAxisLabel ? yAxisLabel : ''} />
        <Tooltip
          content={
            <CustomTooltip
              stackByEntities={stackByEntities}
              stackByType={stackByType}
              isStacked={stacked || grouped}
              xLabelEntities={xLabelEntities}
              xLabelType={xLabelType}
            />
          }
          cursor={TOOLTIP_CONFIG}
          allowEscapeViewBox={{ x: true, y: true }}
          wrapperStyle={{ zIndex: 9999 }}
          position={tooltipY !== undefined ? { y: tooltipY } : undefined}
        />

        {showLegend && (
          <Legend {...legendConfig} formatter={formatLegendValue} />
        )}

        {stacked ? (
          groupKeys.map((key, index) => (
            <Bar
              key={key}
              dataKey={key}
              stackId="a"
              fill={colors[index % colors.length]}
              name={formatLegendValue(key)}
              radius={
                index === groupKeys.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]
              }
              {...STACKED_BAR_CONFIG}
              activeBar={CustomActiveBar}
              // `key` is this segment's own series (stack) — without it a click
              // on one segment is indistinguishable from a click on its whole
              // group. Chart types that are never stacked call the handler with
              // two arguments, which it treats as "the whole group".
              onClick={(sliceData) => handleSliceClick(sliceData, chartData, key)}
            />
          ))
        ) : grouped ? (
          groupKeys.map((key, index) => (
            <Bar
              key={key}
              dataKey={key}
              fill={colors[index % colors.length]}
              name={formatLegendValue(key)}
              {...GROUPED_BAR_CONFIG}
              activeBar={CustomActiveBar}
              onClick={(sliceData) => handleSliceClick(sliceData, chartData, key)}
            />
          ))
        ) : (
          <Bar
            dataKey="value"
            name={tasksLabel}
            {...BAR_CONFIG}
            activeBar={CustomActiveBar}
            onClick={(sliceData) => handleSliceClick(sliceData, chartData)}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={colors[index % colors.length]}
              />
            ))}
          </Bar>
        )}
      </BarChart>
    </ResponsiveContainer>
    </div>
  );
};

export default BarGraph;
